<template>
  <div class="services-page">
    <!-- Hero Section -->
    <section class="page-hero">
      <div class="container">
        <p class="tagline">From code to cloud, security to strategy, we deliver the technology that powers your growth.</p>
      </div>
    </section>

    <!-- Expertise Section -->
    <section class="expertise-section">
      <div class="container">
        <div class="expertise-grid">
          <div class="expertise-text">
            <h2 class="expertise-title">Our Expertise</h2>
            <p class="expertise-lead">Transforming businesses through innovative technology solutions.</p>
            <div class="expertise-content">
              <div class="expertise-point">
                <font-awesome-icon :icon="['fas', 'check-circle']" class="check-icon" />
                <p>Comprehensive technology services tailored to your unique<br>business needs.</p>
              </div>
              <div class="expertise-point">
                <font-awesome-icon :icon="['fas', 'check-circle']" class="check-icon" />
                <p>Deep expertise in cloud, software, mobile, and cybersecurity.</p>
              </div>
              <div class="expertise-point">
                <font-awesome-icon :icon="['fas', 'check-circle']" class="check-icon" />
                <p>Future-ready systems that drive efficiency and unlock growth.</p>
              </div>
            </div>
          </div>
          <div class="mission-approach-container">
            <div class="highlight-card">
              <h3><font-awesome-icon :icon="['fas', 'bullseye']" class="highlight-icon" /> Mission</h3>
              <p>To help organizations harness the power of technology<br>
              to optimize operations, reduce costs, and accelerate<br>
              innovation through scalable, secure solutions.</p>
            </div>
            <div class="highlight-card">
              <h3><font-awesome-icon :icon="['fas', 'handshake']" class="highlight-icon" /> Approach</h3>
              <p>From consulting and architecture design to development<br>
              and ongoing management, we ensure every solution is<br>
              perfectly aligned with your business objectives.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Services Grid -->
    <section class="services-section">
      <div class="container">
        <div class="services-grid">
          <div class="service-card service-card--custom">
            <div class="service-header">
              <div class="service-icon">
                <font-awesome-icon :icon="['fas', 'laptop-code']" />
              </div>
              <h3>Custom Software</h3>
            </div>
            <p>Bespoke software solutions tailored to your specific business needs, built with the latest technologies and best practices.</p>
            <ul class="service-features">
              <li>Enterprise Applications</li>
              <li>Workflow Automation</li>
              <li>System Integration</li>
              <li>Legacy System Modernization</li>
            </ul>
          </div>

          <div class="service-card service-card--mobile">
            <div class="service-header">
              <div class="service-icon">
                <font-awesome-icon :icon="['fas', 'mobile-alt']" />
              </div>
              <h3>Mobile Apps</h3>
            </div>
            <p>Engaging mobile applications for iOS and Android that deliver exceptional user experiences and drive engagement.</p>
            <ul class="service-features">
              <li>Native iOS & Android Apps</li>
              <li>Cross-platform Solutions</li>
              <li>UI/UX Design</li>
              <li>App Store Optimization</li>
            </ul>
          </div>

          <div class="service-card service-card--cloud">
            <div class="service-header">
              <div class="service-icon">
                <font-awesome-icon :icon="['fas', 'cloud']" />
              </div>
              <h3>Cloud Solutions</h3>
            </div>
            <p>Scalable cloud infrastructure and services to optimize your operations and reduce IT overhead.</p>
            <ul class="service-features">
              <li>Cloud Migration</li>
              <li>Serverless Architecture</li>
              <li>Cloud Security</li>
              <li>DevOps & CI/CD</li>
            </ul>
          </div>

          <div class="service-card service-card--cyber">
            <div class="service-header">
              <div class="service-icon">
                <font-awesome-icon :icon="['fas', 'shield-alt']" />
              </div>
              <h3>Cybersecurity</h3>
            </div>
            <p>Comprehensive security solutions to protect your business from evolving cyber threats.</p>
            <ul class="service-features">
              <li>Security Audits</li>
              <li>Penetration Testing</li>
              <li>Compliance Management</li>
              <li>Security Training</li>
            </ul>
          </div>

          <div class="service-card service-card--analytics">
            <div class="service-header">
              <div class="service-icon">
                <font-awesome-icon :icon="['fas', 'chart-line']" />
              </div>
              <h3>Data Analytics</h3>
            </div>
            <p>Turn your data into actionable insights with our advanced analytics solutions.</p>
            <ul class="service-features">
              <li>Business Intelligence</li>
              <li>Big Data Solutions</li>
              <li>Predictive Analytics</li>
              <li>Data Visualization</li>
            </ul>
          </div>

          <div class="service-card service-card--consulting">
            <div class="service-header">
              <div class="service-icon">
                <font-awesome-icon :icon="['fas', 'user-gear']" class="service-fa-icon" />
              </div>
              <h3>IT Consulting</h3>
            </div>
            <p>Expert guidance to help you navigate complex technology decisions and digital transformation.</p>
            <ul class="service-features">
              <li>Technology Strategy</li>
              <li>Digital Transformation</li>
              <li>IT Infrastructure</li>
              <li>Software Selection</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section (match Home.vue) -->
    <section class="cta">
      <div class="container">
        <div class="cta-social" style="order: -1; justify-content: flex-start; text-align: left;">
          <div class="social-title">Follow us!</div>
          <div class="social-icons">
            <a href="#" aria-label="LinkedIn" class="social-icon" target="_blank" rel="noopener">
              <font-awesome-icon :icon="['fab', 'linkedin-in']" />
            </a>
            <!-- <a href="#" aria-label="Instagram" class="social-icon" target="_blank" rel="noopener">
              <font-awesome-icon :icon="['fab', 'instagram']" />
            </a>
            <a href="#" aria-label="Facebook" class="social-icon" target="_blank" rel="noopener">
              <font-awesome-icon :icon="['fab', 'facebook-f']" />
            </a>
            <a href="#" aria-label="Twitter" class="social-icon" target="_blank" rel="noopener">
              <font-awesome-icon :icon="['fab', 'twitter']" />
            </a> -->
          </div>
        </div>
        <div class="cta-content" style="order: 1;">
          <h2>Ready to transform your business?</h2>
          <router-link to="/contact" class="btn btn-white">Let's connect</router-link>
          <div class="cta-footer">Copyright &copy; 2025 Advanced Innovatics Solutions | All rights reserved.</div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'ServicesPage',
  metaInfo: {
    title: 'Our Services - IT Solutions',
    meta: [
      { name: 'description', content: 'Explore our comprehensive IT services including custom software development, mobile apps, cloud solutions, cybersecurity, data analytics, and IT consulting.' }
    ]
  }
}
</script>

<style scoped>
.page-hero {
  background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('../assets/images/image 4.png') no-repeat center center/cover;
  color: white;
  text-align: center;
  padding: 120px 20px;
  margin-bottom: 60px;
}

.page-hero p {
  font-size: 1.25rem;
  max-width: 700px;
  margin: 0 auto;
  opacity: 0.9;
}

.page-hero .tagline {
  font-size: 1.75rem;
  max-width: 800px;
  margin: 0 auto;
  color: #ffffff;
  line-height: 1.4;
}

.services-section {
  padding: 40px 0;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 16px;
}

.service-card {
  background: white;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.service-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.service-icon {
  margin-bottom: 0;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  transform-origin: center;
}

.service-card:hover .service-icon {
  transform: scale(1.15);
  background-color: transparent;
}

.service-card .service-icon svg {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  transform-origin: center;
  color: #0e3273;
  font-size: 1.8rem;
}

.service-card:hover .service-icon svg {
  transform: scale(1.2);
  color: #4361ee;
}

.service-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.service-header .service-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  margin: 0;
}

.service-header h3 { 
  margin: 0;
  line-height: 40px; /* Match icon container height */
  padding: 0;
}

.service-card h3 {
  color: #0e3273;
  margin-bottom: 15px;
  font-size: 1.5rem;
}

.service-card p {
  color: #495057;
  margin-bottom: 20px;
  line-height: 1.6;
}

.service-features {
  list-style: none;
  padding: 0;
  margin: 20px 0 0 0;
}

.service-features li {
  padding: 8px 0;
  color: #6c757d;
  position: relative;
  padding-left: 25px;
}

.service-features li:before {
  content: '✓';
  color: #0e3273;
  font-weight: bold;
  position: absolute;
  left: 0;
}

.expertise-section {
  padding: 60px 0;
  background-color: #f8f9fa;
}

.expertise-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  align-items: start;
}

.expertise-title {
  color: #0e3273;
  font-size: 2.2rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  position: relative;
  display: inline-block;
}

.expertise-title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -10px;
  width: 60px;
  height: 4px;
  background-color: #0e3273;
}

.expertise-lead {
  font-size: 1.3rem;
  color: #2c3e50;
  margin: 1.5rem 0 2rem;
  font-weight: 500;
  line-height: 1.5;
}

.expertise-content {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}

.expertise-point {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.check-icon {
  color: #4361ee;
  margin-top: 4px;
  flex-shrink: 0;
}

.expertise-point p {
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
  color: #495057;
}

.mission-approach-container {
  background: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
  margin: 20px 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.highlight-card {
  padding: 0;
  box-shadow: none;
  background: transparent;
  transition: none;
}

.highlight-card:hover {
  transform: none;
  box-shadow: none;
}

.highlight-card h3 {
  color: #0e3273;
  margin: 0 0 15px 0;
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.highlight-card p {
  color: #495057;
  margin: 0;
  line-height: 1.6;
  font-size: 1rem;
  max-width: 95%;
}

.highlight-icon {
  color: #4361ee;
  margin-right: 8px;
}

.expertise-text {
  padding-top: 0.5em;
}

.cta {
  background: linear-gradient(135deg, #4361ee 0%, #3a0ca3 100%);
  color: white;
  text-align: left;
  padding: 6rem 0;
  margin-top: 4rem;
}

.cta .container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.cta .cta-content {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 1rem;
  flex-wrap: wrap; /* ensure footer wraps to new line under button */
}

.cta .cta-content .cta-footer {
  flex: 0 0 100%;
  text-align: left;
  margin-top: 0.75rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
}

.cta h2 { color: #ffffff; }

.cta .btn-white {
  background: transparent;
  color: #ffffff;
  border: 3px solid #ffffff;
  border-radius: 9999px;
  padding: 0.75rem 1.75rem;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.1;
  text-decoration: none;
}

.cta .btn-white:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.cta .cta-social {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  text-align: left;
}

.cta .cta-social .social-title {
  font-weight: 700;
  font-size: 1.25rem;
  color: #ffffff;
}

.cta .cta-social .social-icons { display: flex; gap: 1rem; }

.cta .cta-social .social-icon {
  width: 64px;
  height: 64px;
  border: 3px solid #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  text-decoration: none;
  font-weight: 800;
  font-size: 1.25rem;
  transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.cta .cta-social .social-icon:hover {
  background: rgba(255, 255, 255, 0.08);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

@media (max-width: 576px) {
  .cta .container { flex-direction: column; align-items: flex-start; }
  .cta .cta-content { flex-direction: column; align-items: flex-start; }
}

@media (max-width: 768px) {
  .expertise-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .expertise-title {
    font-size: 1.8rem;
  }
  
  .expertise-lead {
    font-size: 1.2rem;
  }
  
  .mission-approach-container {
    grid-column: 1 / -1;
  }
  
  .highlight-card {
    max-width: 100%;
  }
}

@media (max-width: 992px) {
  .services-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 576px) {
  .services-grid {
    grid-template-columns: 1fr;
  }
}

/* Ensure consistent styling for all service cards */
.service-card--custom .service-icon,
.service-card--mobile .service-icon,
.service-card--cloud .service-icon,
.service-card--cyber .service-icon,
.service-card--analytics .service-icon,
.service-card--consulting .service-icon {
  font-size: 1.8rem;
  margin-bottom: 0;
}
</style>
